import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { useIntl } from 'react-intl';
import useSearch from 'src/registry/hooks/useSearch';
import { createColumnHelper } from '@tanstack/react-table';
import { generalMessages } from 'src/utils/messages';
import { booleanBadgeOptions } from 'src/utils/constants';
import { useParams } from 'react-router-dom';
import { CustomerPaymentMethod } from 'src/features/paymentMethods/utils/types';
import { useListCustomerPaymentMethods } from 'src/features/paymentMethods/api/useListCustomerPaymentMethods';
import { paymentMethodsMessages } from 'src/features/paymentMethods/utils/messages';
import { PAYMENT_TYPE_METHODS_BADGE_OPTIONS } from 'src/features/paymentMethods/utils/constants';
import CellCustomerPaymentMethodActions from 'src/features/paymentMethods/components/tables/CellCustomerPaymentMethodActions';
import useFilters from 'src/registry/hooks/useFilters';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import React from 'react';
import { CellLinkWithIcon } from 'src/registry/components/table/cells/CellLinkWithIcon';
import useSorting from 'src/registry/hooks/useSorting';
import CreateCustomerPaymentMethodFormModal from 'src/features/paymentMethods/components/forms/CreateCustomerPaymentMethodFormModal';
import usePagination from 'src/registry/hooks/usePagination';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

const CustomerPaymentMethodsTable = () => {
    const { formatMessage } = useIntl();
    const { customerId = '' } = useParams();

    const permissionManageCustomerPaymentMethods = useHasPermissions([PERMISSION.MANAGE_CUSTOMERS]);
    const permissionAccessCustomers = useHasPermissions([PERMISSION.ACCESS_CUSTOMERS]);

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'valid_from', desc: false }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();

    const [dateNow] = React.useState(() => Date.now());

    const {
        data: customerPaymentMethods,
        isLoading,
        isError,
    } = useListCustomerPaymentMethods({
        ...(customerId && { customer: customerId }),
        ...querySearchOptions,
        ...queryFiltersOptions,
        ...querySortingOptions,
        ...queryPaginationOptions,
    });

    const columnHelper = createColumnHelper<CustomerPaymentMethod>();

    const columns = [
        ...(!customerId
            ? [
                  columnHelper.accessor('customer.name', {
                      enableSorting: true,
                      header: DataTableColumnHeader,
                      cell: (ctx) => {
                          return (
                              <CellLinkWithIcon
                                  {...ctx}
                                  to={`/customers/${ctx.row.original.customer.id}`}
                                  hasPermissions={permissionAccessCustomers}
                              />
                          );
                      },
                      meta: {
                          title: formatMessage(paymentMethodsMessages.customer),
                      },
                  }),
              ]
            : []),
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(generalMessages.name),
            },
        }),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            cell: CellBadge,
            enableHiding: true,
            enableColumnFilter: true,
            enableSorting: true,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(paymentMethodsMessages.type),
                badgeOptions: PAYMENT_TYPE_METHODS_BADGE_OPTIONS,
            },
        }),
        columnHelper.accessor('is_primary', {
            header: DataTableColumnHeader,
            cell: CellBadge,
            enableHiding: true,
            enableColumnFilter: true,
            enableSorting: true,
            meta: {
                title: formatMessage(paymentMethodsMessages.isPrimary),
                badgeOptions: booleanBadgeOptions,
            },
        }),
        columnHelper.accessor('valid_from', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableHiding: true,
            enableColumnFilter: true,
            enableSorting: true,
            meta: {
                title: formatMessage(generalMessages.validFrom),
                inputType: INPUT_TYPE.DATE,
                dateFilter: {
                    afterDate: new Date(dateNow - 30 * 24 * 60 * 60 * 1000),
                    beforeDate: new Date(),
                },
                inlineDateFilter: true,
            },
        }),
        columnHelper.accessor('valid_to', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableHiding: true,
            enableColumnFilter: true,
            enableSorting: true,
            meta: {
                title: formatMessage(generalMessages.validTo),
                inputType: INPUT_TYPE.DATE,
                dateFilter: {
                    afterDate: new Date(dateNow - 30 * 24 * 60 * 60 * 1000),
                    beforeDate: new Date(),
                },
                inlineDateFilter: true,
            },
        }),

        ...(permissionManageCustomerPaymentMethods
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellCustomerPaymentMethodActions,
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<CustomerPaymentMethod, unknown>
            columns={columns}
            data={customerPaymentMethods}
            semantics={{
                title: formatMessage(paymentMethodsMessages.paymentMethods),
                entryName: formatMessage(paymentMethodsMessages.method).toLowerCase(),
                entryNames: formatMessage(paymentMethodsMessages.methods).toLowerCase(),
                placeholder: formatMessage(paymentMethodsMessages.method).toLowerCase(),
            }}
            search={search}
            sorting={sorting}
            pagination={pagination}
            filters={filters}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageCustomerPaymentMethods && { newEntry: CreateCustomerPaymentMethodFormModal }),
            }}
        />
    );
};

export default CustomerPaymentMethodsTable;
